import { Link, useNavigate } from "react-router-dom";
import { User, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";

type UserType = "client" | "owner";

export default function Signup() {
  const navigate = useNavigate();
  const { signup, configError } = useApp();
  const [userType, setUserType] = useState<UserType>("client");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setConfirmationMessage("");

    if (!name.trim() || !email.trim() || !password.trim()) {
      setError("Please complete all required fields.");
      return;
    }

    if (!email.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }

    if (password.length < 8) {
      setError("Use at least 8 characters for your password.");
      return;
    }

    setIsSubmitting(true);
    const result = await signup({ role: userType, name, email, password });

    if (!result.ok) {
      setError(result.message ?? "Unable to create account.");
      setIsSubmitting(false);
      return;
    }

    if (result.emailConfirmationRequired) {
      setConfirmationMessage(result.message ?? "Check your email to confirm your account.");
      setIsSubmitting(false);
      return;
    }

    navigate(userType === "owner" ? "/owner/dashboard" : "/customer/dashboard");
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 gradient-hero items-center justify-center p-12">
        <div className="max-w-md text-center">
          <SchedoraLogo iconOnly iconClassName="h-12 w-12 mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-foreground">Join Schedora</h2>
          <p className="mt-3 text-muted-foreground">Start managing your bookings in minutes, with no setup friction.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <SchedoraLogo textClassName="text-xl" />
          </Link>
          <h1 className="text-2xl font-heading font-bold text-foreground">Create account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Choose how you'd like to use Schedora</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { type: "client" as UserType, icon: User, label: "I'm a Client", desc: "Book appointments" },
              { type: "owner" as UserType, icon: Building2, label: "Business Owner", desc: "Manage my business" },
            ].map((opt) => (
              <button
                key={opt.type}
                onClick={() => setUserType(opt.type)}
                className={cn(
                  "rounded-xl border p-4 text-left transition-all hover:border-primary/30",
                  userType === opt.type && "border-primary ring-2 ring-primary/20",
                )}
              >
                <opt.icon className="h-5 w-5 text-primary mb-2" />
                <p className="text-sm font-semibold text-foreground">{opt.label}</p>
                <p className="text-xs text-muted-foreground">{opt.desc}</p>
              </button>
            ))}
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="name">{userType === "owner" ? "Business Name" : "Full Name"}</Label>
              <Input
                id="name"
                placeholder={userType === "owner" ? "Glow Studio" : "Jane Doe"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {confirmationMessage && <p className="text-sm text-success">{confirmationMessage}</p>}
            {configError && <p className="text-xs text-muted-foreground">{configError}</p>}
            <Button className="w-full" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating Account..." : "Create Account"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
